import React, { useState, useRef } from 'react';
import styled, { keyframes, css } from 'styled-components';
import html2canvas from 'html2canvas';

// Keyframe animations
const fadeInDown = keyframes`
  from {
    opacity: 0;
    transform: translateY(-20px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
`;

const sparkle = keyframes`
  0%, 100% { 
    transform: scale(1);
    opacity: 0.7;
  }
  50% { 
    transform: scale(1.2);
    opacity: 1;
  }
`;

const cardFloat = keyframes`
  0%, 100% {
    transform: translateY(0px) rotate(0deg);
  }
  50% {
    transform: translateY(-10px) rotate(2deg);
  }
`;

const iconBounce = keyframes`
  0%, 100% {
    transform: scale(1);
  }
  50% {
    transform: scale(1.2);
  }
`;

const lineGrow = keyframes`
  0%, 100% {
    transform: scaleX(1);
    opacity: 0.7;
  }
  50% {
    transform: scaleX(1.1);
    opacity: 1;
  }
`;

const patternMove = keyframes`
  0% {
    transform: translateX(-100%) translateY(-100%);
  }
  100% {
    transform: translateX(0%) translateY(0%);
  }
`;

// Styled Components
const Container = styled.div`
  min-height: calc(100vh - 120px); /* Account for navbar and footer */
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  padding: 20px;
  font-family: 'Segoe UI', Tahoma, Geneva, Verdana, sans-serif;
  position: relative;
  box-sizing: border-box;

  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background-image: 
      radial-gradient(circle at 25% 25%, rgba(255, 255, 255, 0.1) 2px, transparent 2px),
      radial-gradient(circle at 75% 75%, rgba(255, 255, 255, 0.1) 2px, transparent 2px);
    background-size: 60px 60px;
    pointer-events: none;
  }

  @media (max-width: 768px) {
    padding: 15px;
    min-height: calc(100vh - 140px);
  }

  @media (max-width: 480px) {
    padding: 10px;
    min-height: calc(100vh - 160px);
  }
`;

const Header = styled.div`
  text-align: center;
  color: white;
  margin-bottom: 30px;
  animation: ${fadeInDown} 0.8s ease-out;

  h1 {
    font-size: clamp(2rem, 5vw, 3rem);
    margin-bottom: 10px;
    text-shadow: 2px 2px 4px rgba(0,0,0,0.3);
    font-weight: 700;
    letter-spacing: -0.5px;
  }

  p {
    font-size: clamp(1rem, 2.5vw, 1.3rem);
    opacity: 0.9;
    text-shadow: 0 2px 4px rgba(0, 0, 0, 0.2);
  }
`;

const Content = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 30px;
  max-width: 1400px;
  margin: 0 auto;

  @media (max-width: 1024px) {
    grid-template-columns: 1fr;
    gap: 20px;
    max-width: 800px;
  }
`;

const ControlsPanel = styled.div`
  background: rgba(255, 255, 255, 0.95);
  backdrop-filter: blur(20px);
  border-radius: 20px;
  padding: 30px;
  box-shadow: 0 20px 40px rgba(0,0,0,0.15);
  border: 1px solid rgba(255, 255, 255, 0.2);
  height: fit-content;
  transition: all 0.3s ease;

  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 25px 50px rgba(0,0,0,0.2);
  }
`;

const ControlGroup = styled.div`
  margin-bottom: 25px;

  &:last-child {
    margin-bottom: 0;
  }
`;

const Label = styled.label`
  display: block;
  font-weight: 600;
  margin-bottom: 12px;
  color: #2c3e50;
  font-size: 1.1rem;
  letter-spacing: 0.3px;
`;

const ButtonGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(140px, 1fr));
  gap: 10px;
  align-items: stretch;

  @media (max-width: 768px) {
    grid-template-columns: repeat(2, 1fr);
    gap: 8px;
  }

  @media (max-width: 480px) {
    grid-template-columns: 1fr;
  }
`;

const CategoryButton = styled.button`
  padding: 12px 16px;
  border: 2px solid ${props => props.active ? '#667eea' : '#e1e8ed'};
  border-radius: 12px;
  background: ${props => props.active ? 'linear-gradient(135deg, #667eea, #764ba2)' : 'white'};
  color: ${props => props.active ? 'white' : '#2c3e50'};
  cursor: pointer;
  font-size: 0.95rem;
  font-weight: 500;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  text-align: center;
  display: flex;
  align-items: center;
  justify-content: center;
  white-space: nowrap;
  min-height: 50px;
  position: relative;
  overflow: hidden;

  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: -100%;
    width: 100%;
    height: 100%;
    background: linear-gradient(90deg, transparent, rgba(255,255,255,0.2), transparent);
    transition: left 0.5s;
  }

  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 8px 25px rgba(102, 126, 234, 0.3);
    border-color: #667eea;

    &::before {
      left: 100%;
    }
  }

  &:focus {
    outline: none;
    box-shadow: 0 0 0 3px rgba(102, 126, 234, 0.2);
  }

  @media (max-width: 768px) {
    font-size: 0.9rem;
    padding: 10px 12px;
    min-height: 45px;
  }
`;

const InputField = styled.input`
  width: 100%;
  padding: 14px 16px;
  border: 2px solid #e1e8ed;
  border-radius: 12px;
  font-size: 1rem;
  transition: all 0.3s ease;
  background: white;
  color: #2c3e50;
  box-sizing: border-box;

  &:focus {
    outline: none;
    border-color: #667eea;
    box-shadow: 0 0 0 3px rgba(102, 126, 234, 0.1);
    transform: translateY(-1px);
  }

  &::placeholder {
    color: #95a5a6;
  }
`;

const TextArea = styled.textarea`
  width: 100%;
  padding: 14px 16px;
  border: 2px solid #e1e8ed;
  border-radius: 12px;
  font-size: 1rem;
  resize: vertical;
  min-height: 120px;
  transition: all 0.3s ease;
  font-family: inherit;
  background: white;
  color: #2c3e50;
  box-sizing: border-box;

  &:focus {
    outline: none;
    border-color: #667eea;
    box-shadow: 0 0 0 3px rgba(102, 126, 234, 0.1);
  }

  &::placeholder {
    color: #95a5a6;
  }
`;

const HelperText = styled.small`
  display: block;
  color: #7f8c8d;
  font-size: 0.85rem;
  margin-top: 6px;
  line-height: 1.4;
  font-style: italic;
`;

const SelectField = styled.select`
  width: 100%;
  padding: 14px 16px;
  border: 2px solid #e1e8ed;
  border-radius: 12px;
  font-size: 1rem;
  transition: all 0.3s ease;
  background: white;
  color: #2c3e50;
  box-sizing: border-box;
  cursor: pointer;
  font-family: inherit;

  &:focus {
    outline: none;
    border-color: #667eea;
    box-shadow: 0 0 0 3px rgba(102, 126, 234, 0.1);
    transform: translateY(-1px);
  }

  &:hover {
    border-color: #667eea;
  }

  option {
    padding: 10px;
    font-size: 1rem;
  }
`;

const TemplateGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 10px;
  align-items: stretch;

  @media (max-width: 768px) {
    grid-template-columns: repeat(2, 1fr);
  }

  @media (max-width: 480px) {
    grid-template-columns: 1fr;
  }
`;

const TemplateButton = styled.button`
  padding: 12px;
  border: 2px solid ${props => props.active ? '#667eea' : '#e1e8ed'};
  border-radius: 10px;
  background: ${props => props.active ? 'linear-gradient(135deg, #667eea, #764ba2)' : 'white'};
  color: ${props => props.active ? 'white' : '#2c3e50'};
  cursor: pointer;
  font-size: 0.9rem;
  font-weight: 500;
  transition: all 0.3s ease;
  text-align: center;
  display: flex;
  align-items: center;
  justify-content: center;
  white-space: nowrap;
  min-height: 45px;

  &:hover {
    border-color: #667eea;
    transform: translateY(-1px);
    box-shadow: 0 4px 15px rgba(102, 126, 234, 0.2);
  }

  &:focus {
    outline: none;
    box-shadow: 0 0 0 3px rgba(102, 126, 234, 0.2);
  }
`;

const ColorThemes = styled.div`
  display: grid;
  grid-template-columns: repeat(6, 1fr);
  gap: 12px;
  justify-items: center;

  @media (max-width: 768px) {
    grid-template-columns: repeat(4, 1fr);
    gap: 10px;
  }

  @media (max-width: 480px) {
    grid-template-columns: repeat(3, 1fr);
    gap: 8px;
  }
`;

const ColorButton = styled.button`
  width: 50px;
  height: 50px;
  border: 3px solid ${props => props.active ? '#2c3e50' : 'transparent'};
  border-radius: 50%;
  cursor: pointer;
  transition: all 0.3s ease;
  position: relative;
  overflow: hidden;

  &:hover {
    transform: scale(1.1);
    box-shadow: 0 8px 25px rgba(0,0,0,0.2);
  }

  &:focus {
    outline: none;
    box-shadow: 0 0 0 3px rgba(102, 126, 234, 0.3);
  }
`;

const DownloadButton = styled.button`
  width: 100%;
  padding: 16px;
  background: linear-gradient(135deg, #667eea, #764ba2);
  color: white;
  border: none;
  border-radius: 12px;
  font-size: 1.1rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s ease;
  text-align: center;
  display: flex;
  align-items: center;
  justify-content: center;
  min-height: 55px;
  position: relative;
  overflow: hidden;

  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: -100%;
    width: 100%;
    height: 100%;
    background: linear-gradient(90deg, transparent, rgba(255,255,255,0.2), transparent);
    transition: left 0.5s;
  }

  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 10px 30px rgba(102, 126, 234, 0.4);

    &::before {
      left: 100%;
    }
  }

  &:focus {
    outline: none;
    box-shadow: 0 0 0 3px rgba(102, 126, 234, 0.3);
  }
`;

const CardPreview = styled.div`
  display: flex;
  justify-content: center;
  align-items: flex-start;
  padding: 20px;

  @media (max-width: 1024px) {
    order: -1;
  }
`;

const GreetingCard = styled.div`
  width: 400px;
  height: 600px;
  border-radius: 20px;
  padding: 35px;
  box-shadow: 0 20px 60px rgba(0,0,0,0.3);
  position: relative;
  overflow: hidden;
  display: flex;
  flex-direction: column;
  color: white;
  text-align: center;
  transition: all 0.3s ease;
  background: linear-gradient(135deg, #667eea, #764ba2); /* Default background */
  
  /* Ensure no white background bleeds through */
  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: inherit;
    border-radius: inherit;
    z-index: -1;
  }

  &:hover {
    transform: scale(1.02) translateY(-5px);
    box-shadow: 0 30px 80px rgba(0,0,0,0.4);
  }

  @media (max-width: 768px) {
    width: 350px;
    height: 525px;
    padding: 25px;
  }

  @media (max-width: 480px) {
    width: 300px;
    height: 450px;
    padding: 20px;
  }
`;

// Special balloon-themed birthday card
const BalloonCard = styled(GreetingCard)`
  background: linear-gradient(135deg, #ff9a9e 0%, #fecfef 50%, #fecfef 100%),
              radial-gradient(circle at 20% 80%, rgba(120, 119, 198, 0.3) 0%, transparent 50%),
              radial-gradient(circle at 80% 20%, rgba(255, 119, 198, 0.3) 0%, transparent 50%),
              radial-gradient(circle at 40% 40%, rgba(120, 200, 255, 0.3) 0%, transparent 50%);
  
  /* Balloon decorations scattered across the card */
  &::before {
    content: '🎈 🎈 🎈 🎈 🎈 🎈 🎈 🎈 🎈 🎈 🎈 🎈 🎈 🎈 🎈';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    font-size: 2rem;
    opacity: 0.15;
    z-index: 1;
    display: flex;
    flex-wrap: wrap;
    justify-content: space-around;
    align-content: space-around;
    padding: 20px;
  }
  
  /* Additional floating balloons */
  &::after {
    content: '🎈';
    position: absolute;
    top: 10%;
    right: 10%;
    font-size: 3rem;
    opacity: 0.6;
    z-index: 1;
  }
`;

const CardHeader = styled.div`
  margin-bottom: 30px;
  position: relative;
  z-index: 2;

  @media (max-width: 480px) {
    margin-bottom: 20px;
  }
`;

const CategoryIcon = styled.div`
  font-size: 4rem;
  margin-bottom: 15px;
  filter: drop-shadow(0 4px 8px rgba(0,0,0,0.3));

  @media (max-width: 480px) {
    font-size: 3rem;
    margin-bottom: 10px;
  }
`;

const CardTitle = styled.h2`
  font-size: 2.2rem;
  margin: 0;
  text-shadow: 2px 2px 4px rgba(0,0,0,0.4);
  font-weight: 700;
  letter-spacing: -0.5px;

  @media (max-width: 480px) {
    font-size: 1.8rem;
  }
`;

const CardBody = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  justify-content: center;
  gap: 25px;
  position: relative;
  z-index: 2;

  @media (max-width: 480px) {
    gap: 20px;
  }
`;

const Recipient = styled.div`
  font-size: 1.4rem;
  font-weight: 600;
  text-shadow: 1px 1px 2px rgba(0,0,0,0.3);
  color: rgba(255,255,255,0.95);

  @media (max-width: 480px) {
    font-size: 1.2rem;
  }
`;

const Message = styled.div`
  font-size: 1.15rem;
  line-height: 1.7;
  text-shadow: 1px 1px 2px rgba(0,0,0,0.3);
  padding: 0 10px;
  color: rgba(255,255,255,0.9);
  font-weight: 400;

  @media (max-width: 480px) {
    font-size: 1rem;
    line-height: 1.6;
  }
`;

const Sender = styled.div`
  font-size: 1.3rem;
  font-style: italic;
  text-shadow: 1px 1px 2px rgba(0,0,0,0.3);
  margin-top: 20px;
  font-weight: 500;
  color: rgba(255,255,255,0.95);

  @media (max-width: 480px) {
    font-size: 1.1rem;
    margin-top: 15px;
  }
`;

const CardDecorations = styled.div`
  position: absolute;
  top: 25px;
  right: 25px;
  display: flex;
  gap: 12px;
  z-index: 3;

  span {
    font-size: 1.8rem;
    filter: drop-shadow(0 2px 4px rgba(0,0,0,0.3));
  }

  @media (max-width: 480px) {
    top: 20px;
    right: 20px;
    gap: 8px;

    span {
      font-size: 1.5rem;
    }
  }
`;

const InvitationCard = styled.div`
  width: 400px;
  height: 600px;
  border-radius: 8px;
  padding: 40px;
  box-shadow: 0 15px 40px rgba(0,0,0,0.15);
  position: relative;
  overflow: hidden;
  display: flex;
  flex-direction: column;
  color: #2c3e50;
  text-align: center;
  transition: all 0.3s ease;
  background: linear-gradient(145deg, #ffffff, #f8f9fa);
  border: 3px solid #e8e8e8;
  
  /* Elegant border pattern */
  &::before {
    content: '';
    position: absolute;
    top: 15px;
    left: 15px;
    right: 15px;
    bottom: 15px;
    border: 2px solid #d4af37;
    border-radius: 4px;
    z-index: 1;
  }
  
  /* Inner decorative border */
  &::after {
    content: '';
    position: absolute;
    top: 25px;
    left: 25px;
    right: 25px;
    bottom: 25px;
    border: 1px solid #e8e8e8;
    border-radius: 2px;
    z-index: 1;
  }

  &:hover {
    transform: translateY(-3px);
    box-shadow: 0 20px 50px rgba(0,0,0,0.2);
  }

  @media (max-width: 768px) {
    width: 350px;
    height: 525px;
    padding: 30px;
  }

  @media (max-width: 480px) {
    width: 300px;
    height: 450px;
    padding: 25px;
  }
`;

const InvitationHeader = styled.div`
  margin-bottom: 40px;
  position: relative;
  z-index: 2;
  border-bottom: 2px solid #d4af37;
  padding-bottom: 20px;

  @media (max-width: 480px) {
    margin-bottom: 30px;
    padding-bottom: 15px;
  }
`;

const InvitationIcon = styled.div`
  font-size: 3rem;
  margin-bottom: 15px;
  color: #d4af37;
  filter: drop-shadow(0 2px 4px rgba(0,0,0,0.1));

  @media (max-width: 480px) {
    font-size: 2.5rem;
    margin-bottom: 10px;
  }
`;

const InvitationTitle = styled.h2`
  font-size: 2rem;
  margin: 0;
  color: #2c3e50;
  font-weight: 600;
  letter-spacing: 1px;
  font-family: 'Georgia', serif;

  @media (max-width: 480px) {
    font-size: 1.6rem;
  }
`;

const InvitationBody = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  justify-content: center;
  gap: 30px;
  position: relative;
  z-index: 2;
  padding: 0 10px;

  @media (max-width: 480px) {
    gap: 25px;
  }
`;

const InvitationRecipient = styled.div`
  font-size: 1.3rem;
  font-weight: 500;
  color: #34495e;
  font-style: italic;

  @media (max-width: 480px) {
    font-size: 1.1rem;
  }
`;

const InvitationMessage = styled.div`
  font-size: 1.1rem;
  line-height: 1.8;
  color: #2c3e50;
  font-family: 'Georgia', serif;
  text-align: justify;
  text-justify: inter-word;

  @media (max-width: 480px) {
    font-size: 1rem;
    line-height: 1.6;
  }
`;

const InvitationSender = styled.div`
  font-size: 1.2rem;
  color: #34495e;
  margin-top: 30px;
  font-weight: 500;
  border-top: 1px solid #e8e8e8;
  padding-top: 20px;

  @media (max-width: 480px) {
    font-size: 1rem;
    margin-top: 20px;
    padding-top: 15px;
  }
`;

const InvitationDecorations = styled.div`
  position: absolute;
  top: 35px;
  right: 35px;
  display: flex;
  gap: 10px;
  z-index: 3;

  span {
    font-size: 1.5rem;
    color: #d4af37;
    opacity: 0.7;
  }

  @media (max-width: 480px) {
    top: 30px;
    right: 30px;
    gap: 8px;

    span {
      font-size: 1.2rem;
    }
  }
`;

const GreetingCards = () => {
  const [selectedCategory, setSelectedCategory] = useState('birthday');
  const [recipientName, setRecipientName] = useState('');
  const [senderName, setSenderName] = useState('');
  const [customMessage, setCustomMessage] = useState('');
  const [customTitle, setCustomTitle] = useState('');
  const [selectedTemplate, setSelectedTemplate] = useState(0);
  const [selectedColor, setSelectedColor] = useState('gradient-blue');
  const [recipientType, setRecipientType] = useState('friend');
  const cardRef = useRef(null);

  const recipientTypes = {
    friend: { name: 'Friend', emoji: '👫' },
    family: { name: 'Family', emoji: '👨‍👩‍👧‍👦' },
    partner: { name: 'Partner/Spouse', emoji: '💕' },
    colleague: { name: 'Colleague', emoji: '🤝' },
    child: { name: 'Child', emoji: '👶' },
    parent: { name: 'Parent', emoji: '👨‍👩' },
    teacher: { name: 'Teacher', emoji: '👩‍🏫' },
    student: { name: 'Student', emoji: '🎓' }
  };

  const categories = {
    birthday: {
      name: 'Birthday',
      emoji: '🎂',
      balloonEmoji: '🎈',
      templates: {
        friend: [
          "Happy Birthday to an amazing friend! Hope your special day is filled with wonderful surprises and lots of cake!",
          "Another year older, another year wiser, and still the best friend anyone could ask for! Happy Birthday!",
          "Wishing my awesome friend the happiest of birthdays! May all your dreams come true this year!",
          "Happy Birthday! Thanks for being such an incredible friend. Here's to another year of amazing memories together!"
        ],
        family: [
          "Happy Birthday to someone who means the world to our family! May this year bring you endless joy and blessings.",
          "Celebrating you today and always! Happy Birthday to a wonderful family member who brings so much love to our lives.",
          "Another year of being blessed to have you in our family! Wishing you the happiest birthday ever!",
          "Happy Birthday! Your presence in our family is a gift that keeps on giving. Have a fantastic day!"
        ],
        partner: [
          "Happy Birthday to my one and only! Every day with you is a celebration, but today is extra special.",
          "To my beloved on your birthday - you make every moment magical. Here's to another year of love and adventures together!",
          "Happy Birthday to the love of my life! Thank you for making every day brighter just by being you.",
          "Another year of loving you, and I fall deeper in love every day. Happy Birthday, my darling!"
        ],
        colleague: [
          "Happy Birthday! Wishing you a fantastic day and a year filled with success and happiness.",
          "Hope your birthday is as wonderful as you are to work with! Have a great celebration!",
          "Happy Birthday to a valued colleague! May this new year bring you professional success and personal joy.",
          "Wishing you a very Happy Birthday! Thank you for being such a great team member and making work enjoyable."
        ],
        child: [
          "Happy Birthday to the most wonderful child! May your special day be filled with fun, laughter, and all your favorite things!",
          "Another year of watching you grow and shine! Happy Birthday, little star! Keep being amazing!",
          "Happy Birthday! You bring so much joy and sunshine into everyone's life. Have the best day ever!",
          "Wishing you a birthday as special and wonderful as you are! May all your birthday wishes come true!"
        ],
        parent: [
          "Happy Birthday to the most amazing parent! Thank you for all your love, guidance, and endless support.",
          "Celebrating the person who taught me everything important in life. Happy Birthday! You deserve all the happiness in the world.",
          "Happy Birthday to my hero, my role model, my parent! Grateful for you every single day.",
          "Another year of being blessed with the best parent ever! Wishing you joy, health, and all the love you've given me."
        ],
        teacher: [
          "Happy Birthday to an inspiring teacher! Thank you for making learning fun and helping us grow every day.",
          "Wishing a very Happy Birthday to someone who shapes minds and touches hearts! You're truly appreciated.",
          "Happy Birthday! Your dedication to teaching and caring for your students makes you truly special.",
          "To a wonderful teacher on your birthday - thank you for all the knowledge, patience, and inspiration you share!"
        ],
        student: [
          "Happy Birthday to a bright and talented student! May this year bring you academic success and personal growth.",
          "Wishing you a fantastic birthday! Keep up the great work and continue reaching for the stars!",
          "Happy Birthday! Your curiosity and enthusiasm for learning inspire everyone around you.",
          "Another year older and wiser! Happy Birthday to a student who makes learning a joy to watch!"
        ]
      }
    },
    anniversary: {
      name: 'Anniversary',
      emoji: '💕',
      templates: {
        friend: [
          "Happy Anniversary to a wonderful couple! Your friendship and love story continue to inspire us all.",
          "Celebrating your beautiful journey together! Wishing you many more years of happiness and love.",
          "Happy Anniversary! Your relationship is a testament to true love and friendship. Here's to many more years!",
          "Congratulations on another year of love, laughter, and beautiful memories together!"
        ],
        family: [
          "Happy Anniversary to our beloved family members! Your love brings joy to our entire family.",
          "Celebrating the love that created our beautiful family! Happy Anniversary and here's to many more years!",
          "Happy Anniversary! Your strong bond and love have been the foundation of our family's happiness.",
          "Wishing our dear family members a wonderful anniversary! Your love story continues to inspire us all."
        ],
        partner: [
          "Happy Anniversary, my love! Every year with you gets better and better. Here's to forever!",
          "Another year of being madly in love with you! Happy Anniversary to my everything!",
          "Happy Anniversary! Thank you for being my partner in all of life's adventures. I love you more each day!",
          "Celebrating us today and always! Happy Anniversary to the love of my life!"
        ],
        colleague: [
          "Happy Anniversary! Wishing you and your partner continued happiness and success in your journey together.",
          "Congratulations on your anniversary! May your love continue to grow stronger with each passing year.",
          "Happy Anniversary! Wishing you both joy, love, and many more beautiful years together.",
          "Celebrating your special day! May your anniversary be filled with love and wonderful memories."
        ],
        child: [
          "Happy Anniversary to our wonderful children! Watching your love grow has been such a joy for us.",
          "Celebrating your special day! We're so proud of the beautiful relationship you've built together.",
          "Happy Anniversary! Your love brings such happiness to our family. Wishing you many more years of bliss!",
          "To our dear children on your anniversary - may your love continue to flourish and bring you endless joy!"
        ],
        parent: [
          "Happy Anniversary to the most amazing parents! Your love has been our guiding light all these years.",
          "Celebrating the love that created our family! Happy Anniversary to wonderful parents who show us what true love looks like.",
          "Happy Anniversary! Thank you for being the perfect example of lasting love and commitment.",
          "To our beloved parents on your anniversary - your love story is our favorite fairy tale come true!"
        ],
        teacher: [
          "Happy Anniversary! Wishing you and your partner the same dedication and success in love as you bring to teaching.",
          "Congratulations on your anniversary! May your relationship continue to be as inspiring as your teaching.",
          "Happy Anniversary to a wonderful teacher! May your love story continue to flourish beautifully.",
          "Celebrating your special day! Wishing you happiness, love, and many more wonderful years together."
        ],
        student: [
          "Happy Anniversary! Wishing you both success in love and life as you continue your journey together.",
          "Congratulations on your anniversary! May your love continue to grow as you both grow and learn together.",
          "Happy Anniversary! Your young love is beautiful to see. Wishing you many more happy years ahead!",
          "Celebrating your special milestone! May your love story continue to unfold beautifully!"
        ]
      }
    },
    festival: {
      name: 'Festival',
      emoji: '🎉',
      templates: {
        friend: [
          "Happy Festival! May this celebration bring you and your loved ones joy, prosperity, and wonderful memories!",
          "Festival greetings to an amazing friend! Wishing you peace, happiness, and all the best this season!",
          "May this beautiful festival illuminate your life with happiness and success! Happy celebrations, dear friend!",
          "Sending you warm festival wishes! May this special time bring you closer to all the things that matter most!"
        ],
        family: [
          "Happy Festival to our beloved family! May this auspicious occasion bring blessings and togetherness to our home.",
          "Festival greetings! May our family be blessed with health, happiness, and prosperity throughout this celebration.",
          "Celebrating this beautiful festival with the most wonderful family! May our bonds grow stronger with each celebration.",
          "Happy Festival! Grateful for the blessing of family and the joy we share during these special times."
        ],
        partner: [
          "Happy Festival, my love! May this celebration bring us even closer and fill our life with more joy and blessings.",
          "Festival greetings to my better half! Every celebration is more beautiful when shared with you.",
          "Happy Festival! May this special time bring us prosperity, peace, and endless moments of happiness together.",
          "Celebrating this festival with my favorite person! May our love shine as bright as the festival lights!"
        ],
        colleague: [
          "Happy Festival! Wishing you and your family a joyous celebration filled with prosperity and happiness.",
          "Festival greetings! May this special occasion bring success, peace, and wonderful moments with your loved ones.",
          "Happy Festival! Wishing you all the best during this auspicious time and always.",
          "May this festival bring new opportunities, happiness, and success your way! Enjoy the celebrations!"
        ],
        child: [
          "Happy Festival, little one! May this celebration bring you lots of sweets, fun, and magical moments!",
          "Festival wishes to the brightest star! May this special time fill your world with colors, joy, and laughter!",
          "Happy Festival! May you always find wonder and excitement in these beautiful celebrations!",
          "Wishing you the most wonderful festival ever! May it bring you happiness and all your favorite treats!"
        ],
        parent: [
          "Happy Festival to the most wonderful parent! Thank you for making every celebration special with your love.",
          "Festival greetings! May this celebration bring you the same joy and happiness you've given us throughout the years.",
          "Happy Festival! Your wisdom and love make every celebration more meaningful. Wishing you all the best!",
          "To our beloved parent on this festival - may you be blessed with health, happiness, and all the love you deserve!"
        ],
        teacher: [
          "Happy Festival to an inspiring teacher! May this celebration bring you joy and recognition for all the good you do.",
          "Festival greetings! Wishing you a celebration filled with the same warmth and kindness you bring to your students.",
          "Happy Festival! May this special time bring you rest, joy, and appreciation for your dedicated service.",
          "Celebrating a wonderful teacher! May this festival illuminate your life with happiness and fulfillment!"
        ],
        student: [
          "Happy Festival! May this celebration energize you for all the learning and growth ahead!",
          "Festival greetings to a bright student! May this special time bring you inspiration and joy in your studies!",
          "Happy Festival! May this celebration refresh your mind and spirit for continued academic success!",
          "Wishing you a wonderful festival! May it bring you wisdom, happiness, and motivation for your educational journey!"
        ]
      }
    },
    graduation: {
      name: 'Graduation',
      emoji: '🎓',
      templates: {
        friend: [
          "Congratulations on your graduation, my friend! Your hard work has paid off, and I couldn't be prouder!",
          "You did it! Graduation day is here, and you absolutely deserve this achievement! So proud to call you my friend!",
          "Happy Graduation! Watching you reach this milestone has been incredible. Here's to your bright future ahead!",
          "Congratulations graduate! Your determination and friendship have inspired me. Can't wait to see what you do next!"
        ],
        family: [
          "Congratulations on your graduation! Our family is so proud of your dedication and achievement!",
          "You did it! Graduation day is here, and our entire family celebrates this wonderful milestone with you!",
          "Happy Graduation to someone who makes our family so proud! Your success is our joy!",
          "Congratulations graduate! Your hard work and perseverance have been an inspiration to our whole family!"
        ],
        partner: [
          "Congratulations on your graduation, my love! I'm so proud of your dedication and can't wait to celebrate with you!",
          "You did it, and I'm bursting with pride! Happy Graduation to my amazing partner in life and success!",
          "Happy Graduation! Your achievement is our achievement. So excited for our future together!",
          "Congratulations graduate! Your success makes me love and admire you even more. Here's to our bright future!"
        ],
        colleague: [
          "Congratulations on your graduation! Your dedication to learning while working has been truly admirable!",
          "You did it! Happy Graduation! Your commitment to growth and education is an inspiration to all of us!",
          "Happy Graduation! Wishing you continued success as you apply your new knowledge and skills!",
          "Congratulations graduate! Your achievement shows your dedication and will surely lead to even greater success!"
        ],
        child: [
          "Congratulations on your graduation, superstar! We're so proud of how hard you've worked!",
          "You did it! Happy Graduation to our amazing graduate! You've made us the proudest parents/family ever!",
          "Happy Graduation! Watching you grow and achieve this milestone fills our hearts with so much joy!",
          "Congratulations! Your graduation is just the beginning of all the wonderful things you'll accomplish!"
        ],
        parent: [
          "Congratulations on your graduation! You've shown us that it's never too late to pursue your dreams!",
          "You did it! Happy Graduation! Your determination has been an incredible example for our entire family!",
          "Happy Graduation! We're so proud to have a parent who never stops learning and growing!",
          "Congratulations graduate! Your achievement inspires us all to keep reaching for our goals!"
        ],
        teacher: [
          "Congratulations on your graduation! Thank you for being a teacher who never stops learning!",
          "You did it! Happy Graduation to an educator who leads by example in the pursuit of knowledge!",
          "Happy Graduation! Your commitment to learning makes you an even more inspiring teacher!",
          "Congratulations graduate! Your continued education benefits not just you, but all the students you teach!"
        ],
        student: [
          "Congratulations on your graduation! You've shown incredible dedication and deserve this achievement!",
          "You did it! Happy Graduation! Your hard work and perseverance have truly paid off!",
          "Happy Graduation! This is just the beginning of your amazing journey. So proud of you!",
          "Congratulations graduate! Your achievement is well-earned and your future is bright!"
        ]
      }
    },
    wedding: {
      name: 'Wedding',
      emoji: '💒',
      templates: {
        friend: [
          "Congratulations on your wedding day! So happy to celebrate this beautiful milestone with my dear friend!",
          "Wishing you a lifetime of love and happiness! Your wedding day is perfect, just like your friendship!",
          "Happy Wedding Day! May your marriage be filled with all the joy and laughter you bring to our friendship!",
          "Congratulations! Watching you find your perfect match fills my heart with joy. Here's to your beautiful future!"
        ],
        family: [
          "Congratulations on your wedding! Our family celebrates this joyous union and welcomes love into our hearts!",
          "Happy Wedding Day to our beloved family member! May your marriage bring happiness to our entire family!",
          "Wishing you a lifetime of love and joy! Your wedding fills our family with such happiness and pride!",
          "Congratulations! Your wedding day brings our family together in celebration of love and new beginnings!"
        ],
        partner: [
          "Today we become one! Happy Wedding Day to my soulmate, best friend, and love of my life!",
          "Congratulations to us! Today marks the beginning of our forever journey together as husband and wife!",
          "Happy Wedding Day, my love! I can't wait to spend the rest of my life making you happy!",
          "Today I marry my best friend and greatest love! Here's to our beautiful beginning as a married couple!"
        ],
        colleague: [
          "Congratulations on your wedding! Wishing you and your partner a lifetime filled with love and happiness!",
          "Happy Wedding Day! May your marriage be blessed with joy, understanding, and endless love!",
          "Wishing you both a wonderful wedding celebration and a future filled with happiness together!",
          "Congratulations! May your wedding day be everything you dreamed of and your marriage even more beautiful!"
        ],
        child: [
          "Congratulations on your wedding! We're so proud and happy to see you start this beautiful new chapter!",
          "Happy Wedding Day to our wonderful child! May your marriage be filled with the same love we have for you!",
          "Wishing you both a lifetime of happiness! Your wedding brings such joy to our hearts!",
          "Congratulations! Watching you find love and happiness makes us the proudest parents in the world!"
        ],
        parent: [
          "Congratulations on your wedding! Your love story has been an inspiration to our entire family!",
          "Happy Wedding Day! Your marriage continues to show us what true love and commitment look like!",
          "Wishing you both continued happiness! Your wedding renewal fills our hearts with so much joy!",
          "Congratulations! Your enduring love has been the foundation of our family's happiness!"
        ],
        teacher: [
          "Congratulations on your wedding! May your marriage be as inspiring as your dedication to teaching!",
          "Happy Wedding Day! Wishing you both the same patience, understanding, and joy you bring to education!",
          "Congratulations! May your marriage be filled with learning, growing, and loving together!",
          "Happy Wedding Day to a wonderful teacher! May your new journey be as rewarding as your career!"
        ],
        student: [
          "Congratulations on your wedding! May your marriage be a beautiful lesson in love and partnership!",
          "Happy Wedding Day! Wishing you both success in love as you continue to succeed in life!",
          "Congratulations! May your marriage be filled with growth, learning, and endless happiness together!",
          "Happy Wedding Day! Your love story is just beginning, and it's going to be amazing!"
        ]
      }
    },
    marriageInvitation: {
      name: 'Marriage Invitation',
      emoji: '�',
      templates: {
        friend: [
          "We joyfully invite you to witness our wedding ceremony! Your presence would make our special day complete!",
          "Please join us as we exchange vows and begin our journey as husband and wife! Your friendship means the world to us!",
          "We would be honored to have you celebrate our wedding day with us! Come witness our love story unfold!",
          "Your presence is requested at our wedding ceremony! Let's celebrate love, friendship, and new beginnings together!"
        ],
        family: [
          "With great joy, our families invite you to celebrate the union of our children in holy matrimony!",
          "We cordially request your blessings and presence at the wedding ceremony of our beloved children!",
          "Our hearts are filled with happiness as we invite you to witness this sacred union of our families!",
          "Please join us as our families come together to celebrate this blessed wedding ceremony!"
        ],
        partner: [
          "Together with our families, we invite you to celebrate our wedding day! Join us as we become one in love!",
          "We request the honor of your presence at our wedding ceremony! Your blessings would mean everything to us!",
          "Please join us as we exchange vows and promise our hearts to each other for eternity!",
          "We invite you to witness our love story as we say 'I do' and begin our forever together!"
        ],
        colleague: [
          "We would be delighted to have you join us for our wedding celebration! Your presence would honor us greatly!",
          "You're cordially invited to our wedding ceremony! We hope you can share in our joy and happiness!",
          "Please join us as we celebrate our wedding day! Your good wishes would make our day even more special!",
          "We invite you to witness our wedding ceremony and celebrate this milestone with us!"
        ],
        child: [
          "You're invited to our wedding! Come join us for a celebration filled with love, joy, and lots of fun!",
          "We're getting married and want you to be there! There will be dancing, cake, and wonderful memories!",
          "Please come celebrate our wedding day with us! We can't wait to share this special moment with you!",
          "You're invited to watch us say 'I do'! Join us for a magical day of love and celebration!"
        ],
        parent: [
          "With deep gratitude for your love and guidance, we invite you to bless our wedding ceremony!",
          "We humbly request your presence and blessings as we begin this sacred journey of marriage!",
          "Your love has brought us to this day, and we would be honored to have you witness our wedding!",
          "Please join us for our wedding ceremony! Your blessings and presence are our greatest gifts!"
        ],
        teacher: [
          "We would be honored to have our respected teacher bless our wedding ceremony with your presence!",
          "Your wisdom has guided us, and we invite you to witness this important milestone in our lives!",
          "Please grace our wedding ceremony with your presence! Your blessings would be invaluable to us!",
          "We cordially invite you to celebrate our wedding day! Your guidance has helped shape who we are today!"
        ],
        student: [
          "You're invited to celebrate our wedding day! We hope you can join us for this joyous occasion!",
          "Please join us as we celebrate our marriage! Your presence would add happiness to our special day!",
          "We warmly invite you to witness our wedding ceremony! Come celebrate love and new beginnings with us!",
          "You're cordially invited to our wedding! Let's celebrate this beautiful milestone together!"
        ]
      }
    },
    houseWarming: {
      name: 'House Warming',
      emoji: '🏠',
      templates: {
        friend: [
          "We're excited to invite you to our housewarming celebration! Come see our new home and share in our joy!",
          "Please join us for our housewarming party! We can't wait to show you our new space and celebrate with friends!",
          "You're warmly invited to bless our new home with your presence! Let's celebrate this new chapter together!",
          "We're hosting a housewarming and would love to have you there! Come make our new house feel like home!"
        ],
        family: [
          "Our family joyfully invites you to bless our new home! Your presence would make our housewarming complete!",
          "We cordially invite you to our housewarming ceremony! Come celebrate this blessed milestone with our family!",
          "Please join us as we seek blessings for our new home! Your love and good wishes mean everything to us!",
          "We invite you to grace our new home with your presence and bless this sacred space with your love!"
        ],
        partner: [
          "Join us as we celebrate our new home together! Your presence would make our housewarming even more special!",
          "We're excited to show you our new place! Please come celebrate this milestone in our journey together!",
          "You're invited to our housewarming! Come see where our love story continues to unfold!",
          "Please join us for our housewarming celebration! We can't wait to create new memories in our new home!"
        ],
        colleague: [
          "We would be delighted to have you join us for our housewarming celebration! Come celebrate with us!",
          "You're cordially invited to our housewarming party! We hope you can share in our happiness!",
          "Please join us as we celebrate our new home! Your presence would make our day even more memorable!",
          "We invite you to our housewarming! Come help us create wonderful memories in our new space!"
        ],
        child: [
          "You're invited to our housewarming party! Come see our new home and join us for lots of fun!",
          "We're having a housewarming and want you to be there! There will be games, treats, and exciting surprises!",
          "Please come celebrate our new home with us! We can't wait to show you all the cool things about our house!",
          "You're invited to help us celebrate our new home! Come join the fun and make our house feel magical!"
        ],
        parent: [
          "With gratitude for your love and support, we invite you to bless our new home with your presence!",
          "We humbly request your blessings for our new home! Please join us for our housewarming ceremony!",
          "Your guidance has helped us reach this milestone, and we would be honored to have you bless our new home!",
          "Please join us for our housewarming! Your presence and blessings would sanctify our new dwelling!"
        ],
        teacher: [
          "We would be honored to have our respected teacher bless our new home with your presence!",
          "Your wisdom has guided us to this achievement, and we invite you to celebrate our housewarming!",
          "Please grace our new home with your presence! Your blessings would bring positivity to our space!",
          "We cordially invite you to our housewarming ceremony! Your good wishes would mean so much to us!"
        ],
        student: [
          "You're invited to our housewarming celebration! We hope you can join us for this exciting milestone!",
          "Please come celebrate our new home with us! Your presence would add joy to our housewarming!",
          "We warmly invite you to see our new place! Come join us for a wonderful housewarming celebration!",
          "You're cordially invited to our housewarming! Let's celebrate new beginnings in our new home!"
        ]
      }
    },
    babyShower: {
      name: 'Baby Shower',
      emoji: '👶',
      templates: {
        friend: [
          "You're invited to shower our little miracle with love! Join us for a wonderful baby shower celebration!",
          "Please join us as we celebrate our bundle of joy! Your presence would make our baby shower even more special!",
          "We're excited to invite you to our baby shower! Come help us welcome our little one with love and joy!",
          "You're warmly invited to celebrate our upcoming arrival! Let's shower our baby with blessings together!"
        ],
        family: [
          "Our family joyfully invites you to celebrate our precious baby! Join us for a blessed baby shower!",
          "We cordially invite you to shower our little angel with love and blessings! Your presence would complete our joy!",
          "Please join us as we prepare to welcome our family's newest member! Come celebrate our little miracle!",
          "With hearts full of gratitude, we invite you to bless our baby shower with your loving presence!"
        ],
        partner: [
          "Join us as we celebrate our little one on the way! Your love and support mean everything to us!",
          "We're excited to share this special moment with you! Please come celebrate our baby shower!",
          "You're invited to help us welcome our bundle of joy! Come shower our baby with love and blessings!",
          "Please join us for our baby shower! We can't wait to celebrate our growing family with you!"
        ],
        colleague: [
          "We would be delighted to have you join us for our baby shower! Come celebrate our little miracle!",
          "You're cordially invited to our baby shower celebration! We hope you can share in our happiness!",
          "Please join us as we prepare to welcome our baby! Your presence would make our day even more special!",
          "We invite you to our baby shower! Come help us celebrate this wonderful blessing in our lives!"
        ],
        child: [
          "You're invited to celebrate our baby! Come join us for a fun baby shower with games and treats!",
          "We're having a baby shower and want you to be there! There will be fun activities and yummy cake!",
          "Please come help us welcome our little baby! We're having a special party just for our tiny miracle!",
          "You're invited to our baby shower! Come celebrate our new little family member with us!"
        ],
        parent: [
          "With deep gratitude, we invite you to bless our baby shower! Your love has brought us to this moment!",
          "We humbly request your presence at our baby shower! Your blessings for our child would mean everything!",
          "Please join us as we celebrate your grandchild! Your love and guidance will shape our little one's life!",
          "We invite you to shower our baby with love! Your presence would sanctify this blessed celebration!"
        ],
        teacher: [
          "We would be honored to have our respected teacher celebrate our baby shower with us!",
          "Your nurturing spirit inspires us as future parents! Please join us for our baby shower celebration!",
          "Please grace our baby shower with your presence! Your blessings would guide our child's future learning!",
          "We cordially invite you to celebrate our little scholar-to-be! Come join our baby shower!"
        ],
        student: [
          "You're invited to our baby shower celebration! We hope you can join us for this joyous occasion!",
          "Please come celebrate our baby with us! Your presence would add happiness to our special day!",
          "We warmly invite you to welcome our little one! Come join us for a wonderful baby shower!",
          "You're cordially invited to shower our baby with love! Let's celebrate new life and new beginnings!"
        ]
      }
    },
    engagementParty: {
      name: 'Engagement Party',
      emoji: '💍',
      templates: {
        friend: [
          "We're engaged! Please join us for our engagement party as we celebrate this exciting milestone!",
          "You're invited to celebrate our engagement! Come share in our joy as we begin our journey to marriage!",
          "We said yes to forever! Join us for our engagement party and help us celebrate our love story!",
          "Please come celebrate our engagement with us! Your friendship makes this moment even more special!"
        ],
        family: [
          "Our families joyfully invite you to celebrate our engagement! Come witness the beginning of our union!",
          "We cordially invite you to our engagement ceremony! Your blessings would make this moment complete!",
          "Please join us as we announce our engagement! Your love and support mean the world to us!",
          "With great happiness, we invite you to celebrate our engagement and the joining of our families!"
        ],
        partner: [
          "Join us as we celebrate our engagement! We can't wait to share this magical moment with you!",
          "You're invited to our engagement party! Come celebrate as we take this beautiful step together!",
          "Please join us for our engagement celebration! Your presence would make our happiness complete!",
          "We're excited to announce our engagement! Come celebrate this special milestone in our love story!"
        ],
        colleague: [
          "We would be delighted to have you join us for our engagement celebration! Come share in our happiness!",
          "You're cordially invited to our engagement party! We hope you can celebrate this joyous occasion with us!",
          "Please join us as we celebrate our engagement! Your presence would make our day even more memorable!",
          "We invite you to our engagement party! Come help us celebrate this wonderful milestone!"
        ],
        child: [
          "You're invited to our engagement party! Come celebrate with us as we promise to love each other forever!",
          "We're getting engaged and want you to be there! There will be music, dancing, and lots of fun!",
          "Please come celebrate our engagement with us! We can't wait to share this happy moment with you!",
          "You're invited to help us celebrate our promise of love! Come join our engagement party!"
        ],
        parent: [
          "With deep respect and love, we invite you to celebrate our engagement! Your blessings mean everything!",
          "We humbly request your presence at our engagement ceremony! Your guidance has brought us here!",
          "Please join us for our engagement celebration! Your love and support have made this moment possible!",
          "We invite you to bless our engagement! Your presence would sanctify this sacred commitment!"
        ],
        teacher: [
          "We would be honored to have our respected teacher celebrate our engagement with us!",
          "Your wisdom has guided us to this moment! Please join us for our engagement celebration!",
          "Please grace our engagement party with your presence! Your blessings would be invaluable!",
          "We cordially invite you to witness our engagement! Your guidance has helped shape our future!"
        ],
        student: [
          "You're invited to our engagement celebration! We hope you can join us for this exciting milestone!",
          "Please come celebrate our engagement with us! Your presence would add joy to our special day!",
          "We warmly invite you to our engagement party! Come celebrate love and new beginnings with us!",
          "You're cordially invited to celebrate our promise of forever! Let's make this moment unforgettable!"
        ]
      }
    },
    retirementParty: {
      name: 'Retirement Party',
      emoji: '🎉',
      templates: {
        friend: [
          "You're invited to celebrate a well-deserved retirement! Join us as we honor years of dedication and hard work!",
          "Please join us for a retirement celebration! Come help us toast to new adventures and relaxation ahead!",
          "We're throwing a retirement party and would love to have you there! Let's celebrate this amazing milestone!",
          "You're warmly invited to honor our retiree! Come share stories, laughter, and celebrate their incredible journey!"
        ],
        family: [
          "Our family invites you to celebrate a wonderful retirement! Join us in honoring years of service and dedication!",
          "Please join us as we celebrate this well-earned retirement! Your presence would make this moment special!",
          "We cordially invite you to our retirement celebration! Come honor the end of one chapter and beginning of another!",
          "With great pride, we invite you to celebrate this retirement milestone! Let's toast to new adventures ahead!"
        ],
        partner: [
          "Join us as we celebrate my retirement! I can't wait to start this new chapter of our life together!",
          "You're invited to celebrate our retirement! Come help us toast to freedom and new possibilities!",
          "Please join us for our retirement party! Your support throughout this journey means everything to us!",
          "We're excited to celebrate retirement together! Come share in our joy as we embark on new adventures!"
        ],
        colleague: [
          "You're cordially invited to our retirement celebration! Come honor years of teamwork and friendship!",
          "Please join us as we celebrate a well-deserved retirement! Your presence would make this day complete!",
          "We invite you to our retirement party! Let's celebrate the end of an era and toast to new beginnings!",
          "Come celebrate with us! After years of working together, it's time to honor this incredible achievement!"
        ],
        child: [
          "You're invited to celebrate retirement! Come join us for a party honoring years of hard work!",
          "We're having a retirement party and want you to be there! There will be cake, music, and lots of fun!",
          "Please come celebrate this special milestone with us! We're honoring someone very important!",
          "You're invited to help us celebrate retirement! Come join the fun as we toast to new adventures!"
        ],
        parent: [
          "With deep gratitude, we invite you to celebrate your well-deserved retirement! You've earned this moment!",
          "Please join us as we honor your years of dedication and hard work! Your retirement celebration awaits!",
          "We humbly invite you to celebrate this incredible milestone! Your service and sacrifice inspire us all!",
          "Join us for your retirement party! We're so proud to celebrate this achievement with you!"
        ],
        teacher: [
          "We would be honored to celebrate your retirement! Come let us honor your years of shaping young minds!",
          "Your dedication to education deserves celebration! Please join us for your retirement party!",
          "Please grace us with your presence at your retirement celebration! Your legacy will inspire forever!",
          "We cordially invite you to celebrate your teaching career! Your impact on students is immeasurable!"
        ],
        student: [
          "You're invited to celebrate an amazing retirement! Come honor years of dedication and achievement!",
          "Please join us for this retirement celebration! Your presence would add joy to this special occasion!",
          "We warmly invite you to celebrate this milestone! Come toast to new adventures and well-earned rest!",
          "You're cordially invited to honor this retirement! Let's celebrate years of hard work and dedication!"
        ]
      }
    }
  };

  const colorThemes = {
    'gradient-blue': { primary: '#667eea', secondary: '#764ba2' },
    'gradient-pink': { primary: '#f093fb', secondary: '#f5576c' },
    'gradient-green': { primary: '#4facfe', secondary: '#00f2fe' },
    'gradient-orange': { primary: '#fa709a', secondary: '#fee140' },
    'gradient-purple': { primary: '#a8edea', secondary: '#fed6e3' },
    'gradient-sunset': { primary: '#ffecd2', secondary: '#fcb69f' },
    'gradient-gold': { primary: '#ffd700', secondary: '#ffb347' },
    'gradient-rose': { primary: '#ff9a9e', secondary: '#fecfef' },
    'gradient-teal': { primary: '#00b4db', secondary: '#0083b0' },
    'gradient-royal': { primary: '#4b79a1', secondary: '#283e51' },
    'gradient-coral': { primary: '#ff7f7f', secondary: '#ff6b6b' },
    'gradient-sage': { primary: '#9fccfa', secondary: '#0974f1' }
  };

  const downloadCard = async () => {
    if (cardRef.current) {
      try {
        // Create canvas from the card element
        const canvas = await html2canvas(cardRef.current, {
          backgroundColor: null,
          scale: 2, // Higher quality
          useCORS: true,
          allowTaint: true,
          width: cardRef.current.offsetWidth,
          height: cardRef.current.offsetHeight,
        });

        // Create download link
        const link = document.createElement('a');
        link.download = `card-creator-${selectedCategory}-${Date.now()}.png`;
        link.href = canvas.toDataURL('image/png');
        
        // Trigger download
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        
        // Show success message
        alert('Card downloaded successfully! 🎉');
      } catch (error) {
        console.error('Error downloading card:', error);
        alert('Sorry, there was an error downloading the card. Please try again.');
      }
    }
  };

  const getCurrentIcon = () => {
    // Use balloon icon for template 2 (index 1) of birthday category
    if (selectedCategory === 'birthday' && selectedTemplate === 1) {
      return categories[selectedCategory].balloonEmoji;
    }
    return categories[selectedCategory].emoji;
  };

  const getCurrentMessage = () => {
    if (customMessage.trim()) {
      return customMessage;
    }
    return categories[selectedCategory].templates[recipientType][selectedTemplate];
  };

  const getCurrentTitle = () => {
    if (customTitle.trim()) {
      return customTitle;
    }
    
    // Check if it's any invitation type
    const invitationTypes = ['marriageInvitation', 'houseWarming', 'babyShower', 'engagementParty', 'retirementParty'];
    if (invitationTypes.includes(selectedCategory)) {
      return 'You Are Cordially Invited';
    }
    
    return `Happy ${categories[selectedCategory].name}!`;
  };

  const handleCategoryChange = (newCategory) => {
    setSelectedCategory(newCategory);
    setSelectedTemplate(0); // Reset template selection when category changes
  };

  const handleRecipientTypeChange = (newType) => {
    setRecipientType(newType);
    setSelectedTemplate(0); // Reset template selection when recipient type changes
  };

  return (
    <Container>
      <Header>
        <h1>🎨 Card Creator</h1>
        <p>Create beautiful personalized greeting cards for any occasion!</p>
      </Header>

      <Content>
        <ControlsPanel>
          <ControlGroup>
            <Label>Category:</Label>
            <SelectField
              value={selectedCategory}
              onChange={(e) => handleCategoryChange(e.target.value)}
            >
              {Object.entries(categories).map(([key, category]) => (
                <option key={key} value={key}>
                  {category.emoji} {category.name}
                </option>
              ))}
            </SelectField>
          </ControlGroup>

          <ControlGroup>
            <Label>To Whom:</Label>
            <SelectField
              value={recipientType}
              onChange={(e) => handleRecipientTypeChange(e.target.value)}
            >
              {Object.entries(recipientTypes).map(([key, type]) => (
                <option key={key} value={key}>
                  {type.emoji} {type.name}
                </option>
              ))}
            </SelectField>
          </ControlGroup>

          <ControlGroup>
            <Label>Custom Title (Optional):</Label>
            <InputField
              type="text"
              value={customTitle}
              onChange={(e) => setCustomTitle(e.target.value)}
              placeholder={
                ['marriageInvitation', 'houseWarming', 'babyShower', 'engagementParty', 'retirementParty'].includes(selectedCategory)
                  ? 'Default: "You Are Cordially Invited"'
                  : `Default: "Happy ${categories[selectedCategory].name}!"`
              }
            />
            <HelperText>
              Leave empty to use default title, or customize it (e.g., "Happy Diwali!", "Merry Christmas!", etc.)
            </HelperText>
          </ControlGroup>

          <ControlGroup>
            <Label>Recipient Name:</Label>
            <InputField
              type="text"
              value={recipientName}
              onChange={(e) => setRecipientName(e.target.value)}
              placeholder="Enter recipient's name"
            />
          </ControlGroup>

          <ControlGroup>
            <Label>Your Name:</Label>
            <InputField
              type="text"
              value={senderName}
              onChange={(e) => setSenderName(e.target.value)}
              placeholder="Enter your name"
            />
          </ControlGroup>

          <ControlGroup>
            <Label>Message Template:</Label>
            <SelectField
              value={selectedTemplate}
              onChange={(e) => setSelectedTemplate(parseInt(e.target.value))}
            >
              {categories[selectedCategory].templates[recipientType].map((template, index) => (
                <option key={index} value={index}>
                  Template {index + 1}: {template.substring(0, 50)}...
                </option>
              ))}
            </SelectField>
          </ControlGroup>

          <ControlGroup>
            <Label>Custom Message (Optional):</Label>
            <TextArea
              value={customMessage}
              onChange={(e) => setCustomMessage(e.target.value)}
              placeholder="Write your own message or use the templates above"
              rows="4"
            />
          </ControlGroup>

          <ControlGroup>
            <Label>Color Theme:</Label>
            <ColorThemes>
              {Object.entries(colorThemes).map(([key, theme]) => (
                <ColorButton
                  key={key}
                  active={selectedColor === key}
                  style={{
                    background: `linear-gradient(135deg, ${theme.primary}, ${theme.secondary})`
                  }}
                  onClick={() => setSelectedColor(key)}
                />
              ))}
            </ColorThemes>
          </ControlGroup>

          <ControlGroup>
            <DownloadButton onClick={downloadCard}>
              📱 Download Card
            </DownloadButton>
          </ControlGroup>
        </ControlsPanel>

        <CardPreview>
          {['marriageInvitation', 'houseWarming', 'babyShower', 'engagementParty', 'retirementParty'].includes(selectedCategory) ? (
            <InvitationCard ref={cardRef}>
              <InvitationHeader>
                <InvitationIcon>
                  {categories[selectedCategory].emoji}
                </InvitationIcon>
                <InvitationTitle>{getCurrentTitle()}</InvitationTitle>
              </InvitationHeader>

              <InvitationBody>
                {recipientName && (
                  <InvitationRecipient>
                    Dear {recipientName},
                  </InvitationRecipient>
                )}
                
                <InvitationMessage>
                  {getCurrentMessage()}
                </InvitationMessage>
                
                {senderName && (
                  <InvitationSender>
                    Sincerely,<br />
                    {senderName}
                  </InvitationSender>
                )}
              </InvitationBody>

              <InvitationDecorations>
                <span>❋</span>
                <span>❋</span>
                <span>❋</span>
              </InvitationDecorations>
            </InvitationCard>
          ) : selectedCategory === 'birthday' && selectedTemplate === 1 ? (
            <BalloonCard 
              ref={cardRef}
            >
              <CardHeader>
                <CategoryIcon>
                  {getCurrentIcon()}
                </CategoryIcon>
                <CardTitle>{getCurrentTitle()}</CardTitle>
              </CardHeader>

              <CardBody>
                {recipientName && (
                  <Recipient>
                    Dear {recipientName},
                  </Recipient>
                )}
                
                <Message>
                  {getCurrentMessage()}
                </Message>
                
                {senderName && (
                  <Sender>
                    With love,<br />
                    {senderName}
                  </Sender>
                )}
              </CardBody>

              <CardDecorations>
                <span>🎈</span>
                <span>🎉</span>
                <span>🎈</span>
              </CardDecorations>
            </BalloonCard>
          ) : (
            <GreetingCard 
              ref={cardRef}
              style={{
                background: `linear-gradient(135deg, ${colorThemes[selectedColor].primary}, ${colorThemes[selectedColor].secondary})`
              }}
            >
              <CardHeader>
                <CategoryIcon>
                  {getCurrentIcon()}
                </CategoryIcon>
                <CardTitle>{getCurrentTitle()}</CardTitle>
              </CardHeader>

              <CardBody>
                {recipientName && (
                  <Recipient>
                    Dear {recipientName},
                  </Recipient>
                )}
                
                <Message>
                  {getCurrentMessage()}
                </Message>
                
                {senderName && (
                  <Sender>
                    With love,<br />
                    {senderName}
                  </Sender>
                )}
              </CardBody>

              <CardDecorations>
                <span>✨</span>
                <span>🌟</span>
                <span>✨</span>
              </CardDecorations>
            </GreetingCard>
          )}
        </CardPreview>
      </Content>
    </Container>
  );
};

export default GreetingCards;
